import { Drawer, Input } from 'antd';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { MobileBackButton } from '../../../../assets/icons/common/common-icons';
import { ActionLockedIconWithTooltip } from '../../../../common/components/ActionLocked/ActionLockedIconWithTooltip';
import { LogoLoader } from '../../../../common/components/LogoLoader/LogoLoader';
import { colors } from '../../../../common/styles/theme.styles';
import { useStore } from '../../../app/data/root.store';
import { getDocTypeIcon } from '../../assets/docsTypes/docTypes';
import { MetadataCloseIcon, MetadataDeleteIcon, MetadataDownloadIcon, MetadataEditNameIcon } from '../../assets/icons';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';
import { MetadataFileInfoSection } from './MetadataFileInfoSection';
import { LinkedItemDrawer } from './MetadataLabelsSection/AttachedToItemsTagCategory/LinkedItemModal/LinkedItemDrawer';
import { MetadataLabelsSection } from './MetadataLabelsSection/MetadataLabelsSection';
import { TagEditorDrawer } from './MetadataLabelsSection/TagsTagCategory/TagEditorDrawer';
import { MetadataStatusSection } from './MetadataStatusSection';
import { ButtonsWrapper, DeleteButton, DownloadButton, FileDescriptionWrapper, FileHeader, FileHeaderTitle, FileHeaderTitleEditButton, FileHeaderTitleEditButtonWrapper, FileHeaderTitleInfo, FileHeaderTitleWrapper, FileHeaderTop, FileInfoSubTitle, FileNameEditButtonsGroup, FileNameEditCancelButton, FileNameEditSaveButton, FileTitleWrapper, MetadataPanelContent, MetadataPanelWrapper, MobileMetadataContent, MobileMetadataHeader, Overlay, RequiredText, MobileBackButton as StyledMobileBackButton, DoneButton, CloseButton } from './VaultMetadataPanel.styles';

export const VaultMetadataPanel = observer(() => {
    const { vaultStore, uiStore, userStore } = useStore();
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [fileName, setFileName] = useState(vaultStore.selectedFile?.displayName);
    const [fileDescription, setFileDescription] = useState(vaultStore.selectedFile?.llmResultMetadata?.displayDescription ?? vaultStore.selectedFile?.llmResultMetadata?.description ?? '');
    const [isTagDrawerOpen, setIsTagDrawerOpen] = useState(false);
    const [tagDrawerSelectedTags, setTagDrawerSelectedTags] = useState([]);
    const [isLinkedItemDrawerOpen, setIsLinkedItemDrawerOpen] = useState(false);
    const [linkedItemDrawerSelectedItems, setLinkedItemDrawerSelectedItems] = useState([]);

    const file = vaultStore.selectedFile;
    const isLoading = vaultStore.isLoadingVaultDetails;
    const isSidePanel = uiStore.isVaultMetadataPanelModalThreshold && !vaultStore.isAssetVaultTab;
    const isMobileFullScreen = !uiStore.isDesktopView;

    const fileExtension = file?.originalName?.split('.').pop()?.toLowerCase();

    const handleClose = useCallback(() => {
        setIsAnimating(false);
        setTimeout(() => {
            vaultStore.clearSelectedFile();
            setShouldRender(false);
        }, 250);
    }, [vaultStore]);

    useEffect(() => {
        setFileName(vaultStore.selectedFile?.displayName);
        setFileDescription(vaultStore.selectedFile?.llmResultMetadata?.displayDescription ?? vaultStore.selectedFile?.llmResultMetadata?.description ?? '');
    }, [vaultStore.selectedFile?.displayName, vaultStore.selectedFile?.llmResultMetadata?.displayDescription, vaultStore.selectedFile?.llmResultMetadata?.description]);

    useEffect(() => {
        if (vaultStore.isMetadataPanelOpen) {
            setShouldRender(true);
            setTimeout(() => setIsAnimating(true), 10);
        } else if (shouldRender) {
            setIsAnimating(false);
            setTimeout(() => setShouldRender(false), 250);
        }
    }, [vaultStore.isMetadataPanelOpen, shouldRender]);

    useEffect(() => {
        if (!shouldRender) {
            return;
        }

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [shouldRender, handleClose]);

    if (!shouldRender) {
        return null;
    }

    const handleDownload = async () => {
        try {
            setIsDownloading(true);
            await vaultStore.downloadSelectedFile();
        } catch (error) {
            console.error('Download failed:', error);
            // You might want to show a toast notification here
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDelete = () => {
        if (userStore.isActionBlockedByRole('deleteVaultFile')) {
            userStore.setShowLimitedSharedAccess(true);
            return;
        }
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            setIsDeleting(true);
            await vaultStore.deleteSelectedFile();
            setShowDeleteModal(false);
            handleClose();
        } catch (error) {
            console.error('Delete failed:', error);
            // You might want to show a toast notification here
        } finally {
            setIsDeleting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    const handleFileNameChange = async () => {
        await vaultStore.updateSelectedVaultMetadata({ displayName: fileName, displayDescription: fileDescription })
        setIsEditingName(false);
    };

    const onCancelEditName = () => {
        setIsEditingName(false);
        setFileName(vaultStore.selectedFile?.displayName);
    };

    const openTagEditorDrawer = (selectedTags) => {
        setTagDrawerSelectedTags(selectedTags);
        setIsTagDrawerOpen(true);
    };

    const openLinkedItemDrawer = (selectedItems) => {
        setLinkedItemDrawerSelectedItems(selectedItems);
        setIsLinkedItemDrawerOpen(true);
    };

    const handleSaveTags = async (newTags) => {
        await vaultStore.updateSelectedVaultMetadata({ tags: newTags.map(t => t.label || t.id) });
        setIsTagDrawerOpen(false);
    };

    const handleSaveLinkedItems = async (newItems) => {
        await vaultStore.updateSelectedVaultMetadata({ assetIds: newItems.map(i => i.id) });
        setIsLinkedItemDrawerOpen(false);
    };

    const mainContent = isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <LogoLoader />
        </div>
    ) : (
        <>
            <MetadataStatusSection file={file} />
            <MetadataLabelsSection
                file={file}
                onOpenTagEditorDrawer={openTagEditorDrawer}
                onOpenLinkedItemDrawer={openLinkedItemDrawer}
            />
            <MetadataFileInfoSection file={file} />
        </>
    );

    const isFileNameInvalid = fileName?.trim() === '';

    return (
        <>
            {showDeleteModal && (
                <DeleteConfirmationModal
                    onCancel={() => setShowDeleteModal(false)}
                    onConfirm={confirmDelete}
                    fileName={file?.displayName || "1 item"}
                />
            )}

            {isMobileFullScreen ? (
                <Drawer
                    width="100%"
                    placement="right"
                    closable={false}
                    push={false}
                    onClose={handleClose}
                    visible={isAnimating}
                    destroyOnClose={true}
                    className="vault-metadata-panel-mobile"
                    bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                    <MobileMetadataHeader>
                        <StyledMobileBackButton onClick={handleClose}>
                            <MobileBackButton />
                        </StyledMobileBackButton>
                        {getDocTypeIcon(fileExtension)}
                    </MobileMetadataHeader>
                    <MobileMetadataContent>
                        <FileHeaderTitle onClick={() => setIsEditingName(true)} isEditingName={isEditingName}>
                            <FileHeaderTitleInfo>
                                <FileTitleWrapper isEditingName={isEditingName} isInvalid={isFileNameInvalid}>
                                    <FileInfoSubTitle>Name</FileInfoSubTitle>
                                    <Input.TextArea
                                        id="vault-metadata-panel-file-name-input"
                                        required={true}
                                        value={fileName}
                                        placeholder='Vault name'
                                        onChange={(e) => setFileName(e.target.value)}
                                        style={{
                                            border: 'none',
                                            padding: '0',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            lineHeight: '1.265',
                                            color: colors.darkGray1,
                                            outline: 'none',
                                            boxShadow: 'none',
                                            backgroundColor: 'transparent',
                                            cursor: `${isEditingName ? 'text' : 'pointer'}`,
                                        }}
                                        disabled={!isEditingName}
                                        autoSize={{ minRows: 1, maxRows: 10 }}
                                    />
                                </FileTitleWrapper>
                                <Input.TextArea
                                    id="vault-metadata-panel-file-description-input"
                                    required={true}
                                    value={fileDescription}
                                    onChange={(e) => setFileDescription(e.target.value)}
                                    maxLength={500}
                                    style={{
                                        border: 'none',
                                        width: '100%',
                                        padding: '0',
                                        fontSize: '13px',
                                        fontWeight: '450',
                                        lineHeight: '1.385',
                                        color: colors.darkGray2,
                                        outline: 'none',
                                        boxShadow: 'none',
                                        backgroundColor: 'transparent',
                                        cursor: `${isEditingName ? 'text' : 'pointer'}`,
                                    }}
                                    disabled={!isEditingName}
                                    autoSize={{ minRows: 1, maxRows: 10 }}
                                />
                            </FileHeaderTitleInfo>
                            <FileHeaderTitleEditButtonWrapper>
                                <FileHeaderTitleEditButton>
                                    <MetadataEditNameIcon />
                                </FileHeaderTitleEditButton>
                            </FileHeaderTitleEditButtonWrapper>
                        </FileHeaderTitle>
                        {
                            isEditingName &&
                            <FileNameEditButtonsGroup>
                                <FileNameEditCancelButton onClick={onCancelEditName}>Cancel</FileNameEditCancelButton>
                                <FileNameEditSaveButton disabled={isFileNameInvalid} onClick={handleFileNameChange}>Save</FileNameEditSaveButton>
                            </FileNameEditButtonsGroup>
                        }
                        {mainContent}
                        <ButtonsWrapper>
                            <DownloadButton
                                onClick={handleDownload}
                                disabled={isDownloading || isLoading}
                            >
                                <MetadataDownloadIcon />
                                {isDownloading ? 'Downloading...' : 'Download'}
                            </DownloadButton>
                            <DeleteButton
                                onClick={handleDelete}
                                disabled={isDeleting || isLoading}
                            >
                                <MetadataDeleteIcon />
                                {isDeleting ? 'Deleting...' : 'Delete file'}
                                {userStore.isActionBlockedByRole('deleteVaultFile') && <ActionLockedIconWithTooltip iconColor={'#ACB1BB'} isRoleBlocked={true} marginLeft={'0px'} />}
                            </DeleteButton>
                        </ButtonsWrapper>
                    </MobileMetadataContent>
                    <TagEditorDrawer
                        isOpen={isTagDrawerOpen}
                        onClose={() => setIsTagDrawerOpen(false)}
                        selectedTags={tagDrawerSelectedTags}
                        onSave={handleSaveTags}
                    />
                    <LinkedItemDrawer
                        isOpen={isLinkedItemDrawerOpen}
                        onClose={() => setIsLinkedItemDrawerOpen(false)}
                        fileName={file.displayName}
                        linkedItems={linkedItemDrawerSelectedItems}
                        onSave={handleSaveLinkedItems}
                    />
                </Drawer>
            ) : (
                <>
                    {!isSidePanel && (
                        <Overlay
                            onClick={handleOverlayClick}
                            style={{
                                opacity: isAnimating ? 1 : 0,
                                transition: 'opacity 0.2s ease-out'
                            }}
                        />
                    )}
                    <MetadataPanelWrapper
                        isDesktop={isSidePanel}
                        isAssetVaultTab={vaultStore.isAssetVaultTab}
                        isMobileFullScreen={isMobileFullScreen}
                        isSideMenuMinimized={uiStore.isSideMenuMinimized}
                        className={isAnimating ? 'panel-enter-active' : 'panel-enter'}
                    >
                        <MetadataPanelContent isMobileFullScreen={isMobileFullScreen}>
                            <FileHeaderTitleWrapper>
                                <FileHeader>
                                    <FileHeaderTop>
                                        {getDocTypeIcon(fileExtension)}
                                        {isSidePanel && <CloseButton onClick={handleClose}>
                                            <MetadataCloseIcon />
                                        </CloseButton>}
                                    </FileHeaderTop>
                                    <FileHeaderTitle onClick={() => setIsEditingName(true)} isEditingName={isEditingName}>
                                        <FileHeaderTitleInfo>
                                            <FileTitleWrapper isEditingName={isEditingName} isInvalid={isFileNameInvalid}>
                                                <FileInfoSubTitle>
                                                    <div>Name</div>
                                                    {isFileNameInvalid && <RequiredText>Required</RequiredText>}
                                                </FileInfoSubTitle>
                                                <Input.TextArea
                                                    id="vault-metadata-panel-file-name-input"
                                                    value={fileName}
                                                    placeholder='Vault name'
                                                    onChange={(e) => setFileName(e.target.value)}
                                                    style={{
                                                        border: 'none',
                                                        padding: '0',
                                                        fontSize: '14px',
                                                        fontWeight: '500',
                                                        lineHeight: '1.265',
                                                        color: colors.darkGray1,
                                                        outline: 'none',
                                                        boxShadow: 'none',
                                                        backgroundColor: 'transparent',
                                                        cursor: `${isEditingName ? 'text' : 'pointer'}`,
                                                    }}
                                                    disabled={!isEditingName}
                                                    autoSize={{ minRows: 1, maxRows: 10 }}
                                                />
                                            </FileTitleWrapper>
                                            <FileDescriptionWrapper isEditingName={isEditingName}>
                                                <FileInfoSubTitle>Description / remarks</FileInfoSubTitle>
                                                <Input.TextArea
                                                    id="vault-metadata-panel-file-description-input"
                                                    required={true}
                                                    value={fileDescription}
                                                    onChange={(e) => setFileDescription(e.target.value)}
                                                    maxLength={500}
                                                    style={{
                                                        border: 'none',
                                                        width: '100%',
                                                        padding: '0',
                                                        fontSize: '13px',
                                                        fontWeight: '450',
                                                        lineHeight: '1.385',
                                                        color: colors.darkGray2,
                                                        outline: 'none',
                                                        boxShadow: 'none',
                                                        backgroundColor: 'transparent',
                                                        cursor: `${isEditingName ? 'text' : 'pointer'}`,
                                                    }}
                                                    disabled={!isEditingName}
                                                    autoSize={{ minRows: 1, maxRows: 10 }}
                                                />
                                            </FileDescriptionWrapper>
                                        </FileHeaderTitleInfo>
                                        <FileHeaderTitleEditButtonWrapper>
                                            <FileHeaderTitleEditButton>
                                                <MetadataEditNameIcon />
                                            </FileHeaderTitleEditButton>
                                        </FileHeaderTitleEditButtonWrapper>
                                    </FileHeaderTitle>
                                </FileHeader>
                                {
                                    isEditingName &&
                                    <FileNameEditButtonsGroup>
                                        <FileNameEditCancelButton onClick={onCancelEditName}>Cancel</FileNameEditCancelButton>
                                        <FileNameEditSaveButton disabled={fileName?.trim() === ''} onClick={handleFileNameChange}>Save</FileNameEditSaveButton>
                                    </FileNameEditButtonsGroup>
                                }
                            </FileHeaderTitleWrapper>
                            {mainContent}
                            <ButtonsWrapper>
                                <DownloadButton
                                    onClick={handleDownload}
                                    disabled={isDownloading || isLoading}
                                >
                                    <MetadataDownloadIcon />
                                    {isDownloading ? 'Downloading...' : 'Download'}
                                </DownloadButton>
                                <DeleteButton
                                    onClick={handleDelete}
                                    disabled={isDeleting || isLoading}
                                >
                                    <MetadataDeleteIcon />
                                    {isDeleting ? 'Deleting...' : 'Delete file'}
                                    {userStore.isActionBlockedByRole('deleteVaultFile') && <ActionLockedIconWithTooltip iconColor={'#ACB1BB'} isRoleBlocked={true} marginLeft={'0px'} />}
                                </DeleteButton>
                            </ButtonsWrapper>
                            {!isSidePanel && <DoneButton onClick={handleClose}>
                                Done
                            </DoneButton>}
                        </MetadataPanelContent>
                    </MetadataPanelWrapper>
                </>
            )}
        </>
    );
}); 